import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useStats } from "@/lib/useStats";
import { useUser } from "@/store/user";
import usePageTitle from "@/hooks/usePageTitle";

function UserChip({ onLogin, onLogout, user, isAuthed }) {
  if (isAuthed) {
    return (
      <div className="row" style={{ gap: 8 }} data-testid="user-chip-authed">
        {user.picture && (
          <img
            src={user.picture}
            alt={user.name}
            width={36}
            height={36}
            style={{
              borderRadius: "50%",
              border: "2px solid var(--gold)",
              objectFit: "cover",
            }}
          />
        )}
        <div style={{ fontSize: 13, lineHeight: 1.3 }}>
          <div style={{ fontWeight: 700 }}>{user.name?.split(" ")[0]}</div>
          <div className="muted" style={{ fontSize: 11 }}>{user.email}</div>
        </div>
        <button
          data-testid="logout-btn"
          className="btn btn-outline"
          onClick={onLogout}
          style={{ padding: "8px 12px", fontSize: 13 }}
        >
          خروج
        </button>
      </div>
    );
  }
  return (
    <button
      data-testid="header-login-btn"
      className="btn btn-gold"
      onClick={onLogin}
      style={{ padding: "10px 18px", fontSize: 14 }}
    >
      تسجيل الدخول
    </button>
  );
}

function BrandBar({ onLogin, onLogout, user, isAuthed }) {
  const { progress } = useUser();
  return (
    <div className="brand-bar">
      <div className="brand" data-testid="brand-logo">
        <div className="mark">ب</div>
        <div>
          <div className="title">بَلاغة</div>
          <div className="sub">معلّم البلاغة العربيّة بالذكاء الاصطناعي</div>
        </div>
      </div>
      <div className="row" style={{ gap: 10, justifyContent: "flex-end" }}>
        <div className="stats">
          <span className="stat" data-testid="stat-xp">
            <span className="icon">⭐</span>
            <span>XP</span>
            <span className="v">{progress.xp}</span>
          </span>
          <span className="stat" data-testid="stat-streak">
            <span className="icon">🔥</span>
            <span>سلسلة</span>
            <span className="v">{progress.streak}</span>
          </span>
          <span className="stat" data-testid="stat-badges">
            <span className="icon">🏅</span>
            <span>شارات</span>
            <span className="v">{progress.badges.length}</span>
          </span>
        </div>
        <UserChip
          onLogin={onLogin}
          onLogout={onLogout}
          user={user}
          isAuthed={isAuthed}
        />
      </div>
    </div>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  usePageTitle("بَلاغة — تعلّم البلاغة العربيّة بالذكاء الاصطناعيّ");
  const { progress, user, isAuthed, signOut, subInfo } = useUser();
  const stats = useStats();
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.listLessons()
      .then((res) => {
        const list = Array.isArray(res) ? res : (res?.lessons ?? res?.data ?? []);
        setLessons(Array.isArray(list) ? list : []);
      })
      .catch(() => setLessons([]))
      .finally(() => setLoading(false));
  }, []);

  const completedSet = new Set(progress.completed_lessons || []);
  const totalLessons = lessons.length || 10;
  const progressPct = Math.round((completedSet.size / totalLessons) * 100);
  const nextLesson = lessons.find((l) => !completedSet.has(l.id)) || lessons[0];

  const onLogin = () => navigate("/login");

  return (
    <div className="shell has-bottom-nav" data-testid="home-page">
      <BrandBar
        onLogin={onLogin}
        onLogout={signOut}
        user={user}
        isAuthed={isAuthed}
      />

      <section className="hero" data-testid="hero-section">
        <h1 className="hero-title">
          تعلّم <span className="gold">البلاغة العربيّة</span>
          <br />
          بأسلوبٍ راقٍ، خطوةً بخطوة.
        </h1>
        <p className="lead">
          {stats.lessons} درسًا تفاعليًّا عبر {stats.tracks} مسارات تعليميّة تأخذك من
          أساسيّات البلاغة العربيّة إلى مستويات الإتقان والاحتراف، مع
          مسارٍ تأسيسيٍّ لقراءة التراث البلاغيّ.
          <br />
          مع معلّم ذكاء اصطناعيّ يُحاورك بالعربيّة، ويُقيّم كتاباتك، ويقترح لك
          صياغاتٍ أكثر بلاغةً ووضوحًا.
        </p>

        <div className="home-stats" data-testid="home-stats" style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 14,
          margin: "20px 0 24px",
          fontSize: 14,
        }}>
          {[
            { v: String(stats.lessons), l: "درسًا تفاعليًّا", c: "var(--gold)" },
            { v: String(stats.tracks), l: "مسارات تعليميّة", c: "#2dd4a1" },
            { v: String(stats.tests), l: "اختبارًا", c: "#8b6df0" },
            { v: String(stats.certificates), l: "شهادات رقميّة", c: "#c79c6a" },
          ].map((s, i) => (
            <div key={i} data-testid={`home-stat-${i}`} style={{
              display: "flex", alignItems: "baseline", gap: 6,
              padding: "8px 14px",
              background: "rgba(255,255,255,0.03)",
              border: "1px solid #2a2640",
              borderRadius: 100,
            }}>
              <span style={{ color: s.c, fontWeight: 800, fontSize: 18 }}>{s.v}</span>
              <span className="muted" style={{ fontSize: 13 }}>{s.l}</span>
            </div>
          ))}
        </div>

        <div className="row" style={{ gap: 12 }}>
          <button
            data-testid="hero-start-btn"
            className="btn btn-primary"
            onClick={() => navigate(nextLesson ? `/lesson/${nextLesson.id}` : "/tracks")}
            style={{ padding: "14px 26px", fontSize: 16 }}
          >
            {completedSet.size > 0 ? "تابِع رحلتك ←" : "ابدأ رحلتك ←"}
          </button>
          {!isAuthed && (
            <button
              data-testid="hero-login-btn"
              className="btn btn-ghost"
              onClick={onLogin}
            >
              احفظ تقدّمك بـ Google
            </button>
          )}
        </div>

        <div style={{ marginTop: 28, maxWidth: 520 }}>
          <div
            className="row"
            style={{ justifyContent: "space-between", marginBottom: 8 }}
          >
            <span className="muted" style={{ fontSize: 13 }}>
              تقدّمك الكلّي
            </span>
            <span style={{ fontSize: 13, color: "var(--gold)", fontWeight: 700 }}>
              {completedSet.size}/{totalLessons} درس · {progressPct}%
            </span>
          </div>
          <div className="progress-track" data-testid="overall-progress">
            <div className="progress-fill" style={{ width: `${progressPct}%` }} />
          </div>
        </div>

        {progress.badges?.length > 0 && (
          <div className="badges-strip" data-testid="badges-strip">
            {progress.badges.map((b) => (
              <span key={b.id} className="badge-pill" title={b.name}>
                <span style={{ fontSize: 16 }}>{b.icon}</span>
                {b.name}
              </span>
            ))}
          </div>
        )}
      </section>

      {/* Subscriptions / Pricing section (replaces lesson grid; full grid lives on /tracks) */}
      <h2 className="section-title" data-testid="home-pricing-title">الاشتراكات والأسعار</h2>
      <p className="muted" style={{ maxWidth: 640, lineHeight: 1.8, marginBottom: 24 }}>
        ابدأ مجّانًا، أو افتح المنهج الكامل ({stats.lessons} درسًا + {stats.certificates} شهادات)،
        أو احصل على باقةٍ مؤسّسيّةٍ مُصمَّمةٍ لمعهدك.
      </p>

      <div className="home-pricing-grid" data-testid="home-pricing-grid" style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
        gap: 18,
        marginBottom: 24,
      }}>
        {(() => {
          const coreProduct = subInfo?.products?.find((p) => p.id === "balagha_core");
          const coreMonthly = coreProduct?.plans?.find((p) => p.period === "monthly");
          const corePrice = coreMonthly ? `${coreMonthly.amount_sar} ر.س` : "29 ر.س";
          return [
          {
            id: "free", name: "المجّاني", price: "0 ر.س", period: "للأبد",
            accent: "#2dd4a1", cta: "ابدأ مجّانًا",
            highlights: ["مسار A1 الكامل (8 دروس)", "شهادة A1 PDF", "10 محادثات AI شهريًّا"],
            onClick: () => navigate("/tracks"),
            primary: false,
          },
          {
            id: "premium", name: "Balagha Core", price: corePrice, period: "شهريًّا",
            accent: "#c79c6a", cta: "اكتشف Core", badge: "الأكثر اختيارًا",
            highlights: [
              `بلاغة 1 · 2 · 3 · 4 — المدخل + المعاني + البيان + البديع`,
              `${stats.certificates} شهادات PDF بالعربيّة`,
              "AI غير محدود + تقييم الكتابات",
            ],
            onClick: () => navigate("/pricing"),
            primary: true,
          },
          {
            id: "institutional", name: "مؤسّسيّ", price: "خصصّ", period: "حسب الحاجة",
            accent: "#8b6df0", cta: "تواصل معنا",
            highlights: ["حسابات للطلّاب والمعلّمين", "شهادات بشعار المؤسّسة", "دمجٌ مع LMS"],
            onClick: () => navigate("/partnerships"),
            primary: false,
          },
        ];})().map((t) => (
          <div key={t.id}
               data-testid={`home-pricing-card-${t.id}`}
               className="panel"
               style={{
                 border: t.primary ? `2px solid ${t.accent}` : "1px solid #2a2640",
                 padding: 22,
                 position: "relative",
                 display: "flex",
                 flexDirection: "column",
                 boxShadow: t.primary ? `0 6px 24px ${t.accent}33` : "none",
                 transform: t.primary ? "translateY(-6px)" : "none",
               }}>
            {t.badge && (
              <div style={{
                position: "absolute", top: -11, right: 20,
                background: t.accent, color: "#1a1530",
                padding: "3px 12px", borderRadius: 100,
                fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
              }}>{t.badge}</div>
            )}
            <div style={{ color: t.accent, fontSize: 12, fontWeight: 600, letterSpacing: 1, marginBottom: 6 }}>
              {t.name}
            </div>
            <div style={{ marginBottom: 16 }}>
              <span style={{ fontSize: 32, fontWeight: 700 }}>{t.price}</span>
              <span className="muted" style={{ fontSize: 13, marginInlineStart: 6 }}>/ {t.period}</span>
            </div>
            <ul style={{ listStyle: "none", padding: 0, marginBottom: 18, flex: 1 }}>
              {t.highlights.map((h, i) => (
                <li key={i} style={{ display: "flex", gap: 8, marginBottom: 8, fontSize: 13, lineHeight: 1.55 }}>
                  <span style={{ color: t.accent, flexShrink: 0 }}>✓</span>
                  <span>{h}</span>
                </li>
              ))}
            </ul>
            <button
              data-testid={`home-pricing-cta-${t.id}`}
              onClick={t.onClick}
              className={t.primary ? "btn btn-primary" : "btn btn-ghost"}
              style={{
                background: t.primary ? t.accent : "transparent",
                color: t.primary ? "#1a1530" : t.accent,
                border: t.primary ? "none" : `1px solid ${t.accent}`,
                fontWeight: 600, padding: "10px",
              }}>
              {t.cta}
            </button>
          </div>
        ))}
      </div>

      <div style={{ textAlign: "center", marginBottom: 18 }}>
        <button
          data-testid="home-view-pricing-link"
          onClick={() => navigate("/pricing")}
          style={{
            background: "none", border: "none",
            color: "var(--gold)", cursor: "pointer",
            font: "inherit", textDecoration: "underline", textUnderlineOffset: 3,
            fontSize: 14,
          }}
        >
          عرضُ تفاصيل الخطط والأسئلة الشائعة →
        </button>
      </div>

      {/* Pre-footer prominent CTA */}
      <section
        data-testid="home-final-cta-section"
        style={{
          textAlign: "center",
          margin: "30px auto 40px",
          padding: "36px 24px",
          maxWidth: 720,
          background: "linear-gradient(135deg, rgba(199,156,106,0.12), rgba(45,212,161,0.08))",
          border: "1px solid #2a2640",
          borderRadius: 18,
        }}
      >
        <h3 style={{ fontSize: "clamp(20px, 3vw, 26px)", marginBottom: 10, lineHeight: 1.5 }}>
          ابدأ رحلتك في البلاغة العربيّة <span className="gold">اليوم</span>
        </h3>
        <p className="muted" style={{ marginBottom: 22, lineHeight: 1.8, fontSize: 14 }}>
          مسار A1 الكامل مجّانًا — بدون بطاقة ائتمان، بدون التزام. اربح أوّل شهادةٍ
          PDF خلال أيّام.
        </p>
        <button
          data-testid="home-start-free-cta"
          className="btn btn-primary"
          onClick={() => navigate("/tracks")}
          style={{
            padding: "14px 36px",
            fontSize: 17,
            fontWeight: 700,
            borderRadius: 100,
            boxShadow: "0 6px 20px rgba(199,156,106,0.35)",
          }}
        >
          ابدأ مجانًا ←
        </button>
      </section>

      <div
        className="muted"
        style={{ textAlign: "center", marginTop: 16, fontSize: 13 }}
      >
        صُنع بحبٍّ — تطبيق بَلاغة · لتعليم العربيّة بأسلوبٍ راقٍ. ·{" "}
        <button
          data-testid="home-about-link"
          onClick={() => navigate("/about")}
          style={{
            background: "none",
            border: "none",
            color: "var(--gold)",
            cursor: "pointer",
            font: "inherit",
            padding: 0,
            textDecoration: "underline",
            textUnderlineOffset: 3,
          }}
        >
          عن المشروع
        </button>
      </div>
    </div>
  );
}
